import { useState, useEffect } from 'react';
import { Plus, Trash2, Link2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { DatePicker } from '../ui/date-picker';
import { parseISO, format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface DataEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any[]) => void;
  data: any[];
  componentType: string;
  columns?: any[];
  title?: string;
  config?: any; // Configuração completa do componente (para relationship-table)
  allComponents?: any[]; // Todos os componentes para buscar dados de lookup
}

export default function DataEditModal({
  isOpen,
  onClose,
  onSave,
  data,
  componentType,
  columns = [],
  title = 'Editar Dados',
  config,
  allComponents = [],
}: DataEditModalProps) {
  const [editedData, setEditedData] = useState<any[]>([]);

  useEffect(() => {
    setEditedData(JSON.parse(JSON.stringify(data || [])));
  }, [data, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedData);
    onClose();
  };

  const renderChartEditor = () => (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Pontos de Dados</h3>
        <Button
          type="button"
          size="sm"
          onClick={() => setEditedData([...editedData, { name: '', value: 0 }])}
        >
          <Plus className="w-4 h-4 mr-1" />
          Adicionar Ponto
        </Button>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {editedData.map((item, index) => (
          <div key={index} className="flex gap-2 items-center p-3 border rounded bg-muted/30">
            <Input
              type="text"
              placeholder="Nome/Categoria"
              value={item.name || ''}
              onChange={(e) => {
                const newData = [...editedData];
                newData[index] = { ...newData[index], name: e.target.value };
                setEditedData(newData);
              }}
              className="flex-1"
            />
            <Input
              type="number"
              placeholder="Valor"
              value={item.value || 0}
              onChange={(e) => {
                const newData = [...editedData];
                newData[index] = { ...newData[index], value: Number(e.target.value) };
                setEditedData(newData);
              }}
              className="w-32"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setEditedData(editedData.filter((_, i) => i !== index))}
              className="text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {editedData.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            Nenhum ponto adicionado. Clique em "Adicionar Ponto" para começar.
          </p>
        )}
      </div>
    </>
  );

  const renderTableEditor = () => (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Linhas da Tabela</h3>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const newRow: any = {};
            columns.forEach((col: any) => {
              // Aplicar valor padrão se definido
              newRow[col.field] = col.defaultValue !== undefined ? col.defaultValue : '';
            });
            setEditedData([...editedData, newRow]);
          }}
          disabled={columns.length === 0}
        >
          <Plus className="w-4 h-4 mr-1" />
          Adicionar Linha
        </Button>
      </div>
      {columns.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">
          Configure as colunas primeiro nas configurações do componente.
        </p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {editedData.map((row, rowIndex) => (
            <div key={rowIndex} className="p-4 border rounded bg-muted/30">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">Linha {rowIndex + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditedData(editedData.filter((_, i) => i !== rowIndex))}
                  className="h-7 w-7 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {columns
                  .filter((col: any) => col.field.toLowerCase() !== 'id') // Ocultar campo id (case-insensitive)
                  .map((col: any) => {
                  // Para colunas lookup, renderizar Select
                  if (col.type === 'lookup' && col.lookupTable) {
                    const lookupComponent = allComponents.find(
                      (c: any) => c.id === col.lookupTable.tableId
                    );
                    const lookupData = lookupComponent?.config?.dataSource?.data || [];
                    
                    return (
                      <div key={col.field} className="space-y-1">
                        <Label className="text-xs">{col.label}</Label>
                        <Select
                          value={row[col.field]?.toString() || (col.defaultValue?.toString() || '')}
                          onValueChange={(value) => {
                            const newData = [...editedData];
                            newData[rowIndex] = { ...newData[rowIndex], [col.field]: value };
                            setEditedData(newData);
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {lookupData.map((item: any) => {
                              const keyValue = item[col.lookupTable.keyField];
                              const displayValue = item[col.lookupTable.displayField];
                              return (
                                <SelectItem key={keyValue} value={keyValue?.toString()}>
                                  {displayValue}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  
                  // Para colunas tableLink, renderizar dois inputs: Nome e URL
                  if (col.type === 'tableLink') {
                    const val = row[col.field];
                    const parsed = val && typeof val === 'object' ? val : {};
                    return (
                      <div key={col.field} className="col-span-2 space-y-1">
                        <Label className="text-xs">{col.label}</Label>
                        <Input
                          type="text"
                          placeholder="Nome"
                          value={parsed.name || ''}
                          onChange={(e) => {
                            const newData = [...editedData];
                            newData[rowIndex] = { ...newData[rowIndex], [col.field]: { ...parsed, name: e.target.value } };
                            setEditedData(newData);
                          }}
                          className="h-9"
                        />
                        <Input
                          type="text"
                          placeholder="URL do Google Sheets"
                          value={parsed.sheetsUrl || ''}
                          onChange={(e) => {
                            const newData = [...editedData];
                            newData[rowIndex] = { ...newData[rowIndex], [col.field]: { ...parsed, sheetsUrl: e.target.value } };
                            setEditedData(newData);
                          }}
                          disabled={!!parsed.pageSlug}
                          className="h-9"
                        />
                        {parsed.pageSlug && (
                          <p className="text-xs text-muted-foreground">
                            Página: /pages/{parsed.pageSlug}
                          </p>
                        )}
                      </div>
                    );
                  }

                  // Para outros tipos de coluna, renderizar Input
                  return (
                    <div key={col.field} className="space-y-1">
                      <Label className="text-xs">{col.label}</Label>
                      {col.type === 'date' ? (
                        <DatePicker
                          date={row[col.field] ? parseISO(row[col.field]) : undefined}
                          onDateChange={(date) => {
                            const newData = [...editedData];
                            newData[rowIndex] = { ...newData[rowIndex], [col.field]: date ? format(date, "yyyy-MM-dd") : "" };
                            setEditedData(newData);
                          }}
                          placeholder="Select date"
                        />
                      ) : (
                        <Input
                          type={col.type === 'number' ? 'number' : 'text'}
                          value={row[col.field] || ''}
                          onChange={(e) => {
                            const newData = [...editedData];
                            newData[rowIndex] = { ...newData[rowIndex], [col.field]: e.target.value };
                            setEditedData(newData);
                          }}
                          className="h-9"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {editedData.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma linha adicionada. Clique em "Adicionar Linha" para começar.
            </p>
          )}
        </div>
      )}
    </>
  );

  const renderListEditor = () => (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Itens da Lista</h3>
        <Button
          type="button"
          size="sm"
          onClick={() =>
            setEditedData([
              ...editedData,
              { id: Date.now().toString(), title: '', subtitle: '', icon: '', badge: '' },
            ])
          }
        >
          <Plus className="w-4 h-4 mr-1" />
          Adicionar Item
        </Button>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {editedData.map((item, index) => (
          <div key={index} className="p-4 border rounded bg-muted/30">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Item {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setEditedData(editedData.filter((_, i) => i !== index))}
                className="h-7 w-7 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Título (obrigatório)"
                value={item.title || ''}
                onChange={(e) => {
                  const newData = [...editedData];
                  newData[index] = { ...newData[index], title: e.target.value };
                  setEditedData(newData);
                }}
              />
              <Input
                type="text"
                placeholder="Subtítulo (opcional)"
                value={item.subtitle || ''}
                onChange={(e) => {
                  const newData = [...editedData];
                  newData[index] = { ...newData[index], subtitle: e.target.value };
                  setEditedData(newData);
                }}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="text"
                  placeholder="Ícone (emoji)"
                  value={item.icon || ''}
                  onChange={(e) => {
                    const newData = [...editedData];
                    newData[index] = { ...newData[index], icon: e.target.value };
                    setEditedData(newData);
                  }}
                />
                <Input
                  type="text"
                  placeholder="Badge"
                  value={item.badge || ''}
                  onChange={(e) => {
                    const newData = [...editedData];
                    newData[index] = { ...newData[index], badge: e.target.value };
                    setEditedData(newData);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        {editedData.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            Nenhum item adicionado. Clique em "Adicionar Item" para começar.
          </p>
        )}
      </div>
    </>
  );

  const renderMetricEditor = () => (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Valor da Métrica</Label>
          <Input
            type="number"
            placeholder="Digite o valor"
            value={editedData[0]?.value || 0}
            onChange={(e) => {
              setEditedData([{ value: Number(e.target.value) }]);
            }}
          />
          <p className="text-sm text-muted-foreground">
            Este é o valor estático da métrica. Para valores calculados dinamicamente a partir de tabelas, use as configurações do componente.
          </p>
        </div>
      </div>
    </>
  );

  const renderKanbanEditor = () => {
    const columns = editedData || [];

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Colunas do Kanban</h3>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setEditedData([
                ...columns,
                {
                  id: `col-${Date.now()}`,
                  title: 'Nova Coluna',
                  icon: '📋',
                  cards: [],
                },
              ]);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Coluna
          </Button>
        </div>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {columns.map((column: any, columnIndex: number) => (
            <div key={columnIndex} className="p-4 border-2 rounded-lg bg-background">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="text"
                    placeholder="Ícone"
                    value={column.icon || ''}
                    onChange={(e) => {
                      const newData = [...columns];
                      newData[columnIndex] = { ...newData[columnIndex], icon: e.target.value };
                      setEditedData(newData);
                    }}
                    className="w-16 text-center"
                  />
                  <Input
                    type="text"
                    placeholder="Título da coluna"
                    value={column.title || ''}
                    onChange={(e) => {
                      const newData = [...columns];
                      newData[columnIndex] = { ...newData[columnIndex], title: e.target.value };
                      setEditedData(newData);
                    }}
                    className="flex-1 font-semibold"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditedData(columns.filter((_: any, i: number) => i !== columnIndex))}
                  className="ml-2 text-destructive hover:bg-destructive/10"
                  title="Excluir coluna"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="ml-4 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Cards ({column.cards?.length || 0})</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newData = [...columns];
                      newData[columnIndex] = {
                        ...newData[columnIndex],
                        cards: [
                          ...(newData[columnIndex].cards || []),
                          {
                            id: `card-${Date.now()}`,
                            title: 'Novo Card',
                            description: '',
                            badge: '',
                            color: 'blue',
                          },
                        ],
                      };
                      setEditedData(newData);
                    }}
                    className="h-7"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Card
                  </Button>
                </div>

                <div className="space-y-2">
                  {(column.cards || []).map((card: any, cardIndex: number) => (
                    <div key={cardIndex} className="p-3 bg-muted/30 border rounded">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Card {cardIndex + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newData = [...columns];
                            newData[columnIndex] = {
                              ...newData[columnIndex],
                              cards: column.cards.filter((_: any, i: number) => i !== cardIndex),
                            };
                            setEditedData(newData);
                          }}
                          className="h-6 w-6 text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder="Título do card"
                          value={card.title || ''}
                          onChange={(e) => {
                            const newData = [...columns];
                            const updatedCards = [...column.cards];
                            updatedCards[cardIndex] = { ...updatedCards[cardIndex], title: e.target.value };
                            newData[columnIndex] = { ...newData[columnIndex], cards: updatedCards };
                            setEditedData(newData);
                          }}
                          className="text-sm"
                        />
                        <Textarea
                          placeholder="Descrição"
                          value={card.description || ''}
                          onChange={(e) => {
                            const newData = [...columns];
                            const updatedCards = [...column.cards];
                            updatedCards[cardIndex] = { ...updatedCards[cardIndex], description: e.target.value };
                            newData[columnIndex] = { ...newData[columnIndex], cards: updatedCards };
                            setEditedData(newData);
                          }}
                          rows={2}
                          className="text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="text"
                            placeholder="Badge"
                            value={card.badge || ''}
                            onChange={(e) => {
                              const newData = [...columns];
                              const updatedCards = [...column.cards];
                              updatedCards[cardIndex] = { ...updatedCards[cardIndex], badge: e.target.value };
                              newData[columnIndex] = { ...newData[columnIndex], cards: updatedCards };
                              setEditedData(newData);
                            }}
                            className="text-sm"
                          />
                          <Select
                            value={card.color || 'blue'}
                            onValueChange={(value) => {
                              const newData = [...columns];
                              const updatedCards = [...column.cards];
                              updatedCards[cardIndex] = { ...updatedCards[cardIndex], color: value };
                              newData[columnIndex] = { ...newData[columnIndex], cards: updatedCards };
                              setEditedData(newData);
                            }}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Cor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="red">Vermelho</SelectItem>
                              <SelectItem value="yellow">Amarelo</SelectItem>
                              <SelectItem value="green">Verde</SelectItem>
                              <SelectItem value="blue">Azul</SelectItem>
                              <SelectItem value="purple">Roxo</SelectItem>
                              <SelectItem value="gray">Cinza</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {columns.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma coluna criada. Clique em "Adicionar Coluna" para começar.
            </p>
          )}
        </div>
      </>
    );
  };

  const renderRelationshipTableEditor = () => {
    const leftTable = config?.leftTable || { id: '', name: 'Tabela A', data: [], keyField: 'id', displayField: 'name' };
    const rightTable = config?.rightTable || { id: '', name: 'Tabela B', data: [], keyField: 'id', displayField: 'name' };
    const leftKeyField = config?.leftKeyField || 'leftId';
    const rightKeyField = config?.rightKeyField || 'rightId';
    const additionalColumns = config?.columns || [];

    // Criar lookup maps para exibição
    const leftTableMap = new Map();
    leftTable.data.forEach((item: any) => {
      leftTableMap.set(item[leftTable.keyField], item[leftTable.displayField]);
    });

    const rightTableMap = new Map();
    rightTable.data.forEach((item: any) => {
      rightTableMap.set(item[rightTable.keyField], item[rightTable.displayField]);
    });

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Relacionamentos
          </h3>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const newRelationship: any = {
                [leftKeyField]: '',
                [rightKeyField]: '',
              };
              additionalColumns.forEach((col: any) => {
                newRelationship[col.field] = '';
              });
              setEditedData([...editedData, newRelationship]);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Relacionamento
          </Button>
        </div>

        {leftTable.data.length === 0 || rightTable.data.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            Configure as tabelas de origem primeiro nas configurações do componente.
          </p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {editedData.map((relationship, index) => (
              <div key={index} className="p-4 border rounded bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Relacionamento {index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditedData(editedData.filter((_, i) => i !== index))}
                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-3">
                  {/* Select para leftTable */}
                  <div className="space-y-1">
                    <Label className="text-xs">{leftTable.name}</Label>
                    <Select
                      value={relationship[leftKeyField]?.toString() || ''}
                      onValueChange={(value) => {
                        const newData = [...editedData];
                        newData[index] = { ...newData[index], [leftKeyField]: value };
                        setEditedData(newData);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={`Selecione ${leftTable.name.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {leftTable.data.map((item: any) => (
                          <SelectItem 
                            key={item[leftTable.keyField]} 
                            value={item[leftTable.keyField].toString()}
                          >
                            {item[leftTable.displayField]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Select para rightTable */}
                  <div className="space-y-1">
                    <Label className="text-xs">{rightTable.name}</Label>
                    <Select
                      value={relationship[rightKeyField]?.toString() || ''}
                      onValueChange={(value) => {
                        const newData = [...editedData];
                        newData[index] = { ...newData[index], [rightKeyField]: value };
                        setEditedData(newData);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={`Selecione ${rightTable.name.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {rightTable.data.map((item: any) => (
                          <SelectItem 
                            key={item[rightTable.keyField]} 
                            value={item[rightTable.keyField].toString()}
                          >
                            {item[rightTable.displayField]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Campos adicionais */}
                  {additionalColumns.length > 0 && (
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      {additionalColumns.map((col: any) => (
                        <div key={col.field} className="space-y-1">
                          <Label className="text-xs">{col.label}</Label>
                          <Input
                            type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                            value={relationship[col.field] || ''}
                            onChange={(e) => {
                              const newData = [...editedData];
                              newData[index] = { ...newData[index], [col.field]: e.target.value };
                              setEditedData(newData);
                            }}
                            className="h-9"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {editedData.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                Nenhum relacionamento adicionado. Clique em "Adicionar Relacionamento" para começar.
              </p>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex-1 overflow-y-auto py-4">
            {componentType === 'chart' && renderChartEditor()}
            {componentType === 'table' && renderTableEditor()}
            {componentType === 'relationship-table' && renderRelationshipTableEditor()}
            {componentType === 'list' && renderListEditor()}
            {componentType === 'kanban' && renderKanbanEditor()}
            {componentType === 'metric-card' && renderMetricEditor()}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              Salvar Dados
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
